import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { GoogleAnalyticsContext } from '../../GoogleAnalytics';
import { useStepper } from '../../Stepper';
import epoTransitMrnPng from '../assets/invoices.png';
import paperclipIcon from '../assets/paperclip.png';
import StepWrapper from './StepWrapper';
import { Input } from '../../Input/Input';
import _ from 'lodash';
import { getIsValidMrn, getIsValidFiletype } from '../../../utils/stringValidation';

const fileErrors = {
  none: 'NONE',
  fileType: 'FILE_TYPE',
  noneSelected: 'NONE_SELECTED',
};

export const TransitMrn = ({ DDAStatus }) => {
  const { stepForward, localData, setDataByKey, activeStepKey, stepBackward, unvisitStep, gotoStep } = useStepper();

  const [mrnNumberError, setMrnNumberError] = useState(false);
  const [fileErrorType, setFileErrorType] = useState(fileErrors.none);

  const getUploadButtonSelector = () => {
    if (fileErrorType !== fileErrors.none) {
      return 'error';
    }
    if (localData?.mrnDocuments.length) {
      return 'hasFile';
    }
    return '';
  };

  const validateMrnNumber = (value) => {
    const isValidMrn = getIsValidMrn(value);
    setMrnNumberError(!isValidMrn);

    return isValidMrn;
  };

  const debouncedValidateMrnNumber = useCallback(_.debounce(validateMrnNumber, 500), []);

  const handleAddFiles = (files) => {
    const fileList = [];

    for (const file of files) {
      const { name } = file;
      if (getIsValidFiletype(name)) {
        fileList.push({ name: 'Transit Document', file });
      } else {
        setFileErrorType(fileErrors.fileType);
      }
    }
    setDataByKey('mrnDocuments', fileList);
  };

  const handleChangeRadio = (e) => {
    setDataByKey('hasTransitMrn', e.target.value);
    if (e.target.value === 'no') {
      setDataByKey('transitMrn', '');
      setDataByKey('mrnDocuments', []);
      setMrnNumberError(false);
      setFileErrorType(fileErrors.none);
    }
  };

  const handleChangeMrnNumber = (value) => {
    const upperValue = value.toUpperCase();
    setDataByKey('transitMrn', upperValue);
    debouncedValidateMrnNumber(upperValue);
  };

  const nextPage = () => {
    if (localData.hasTransitMrn === 'no') {
      stepForward();
    } else if (localData.hasTransitMrn === 'yes') {
      const isValidMrnNumber = validateMrnNumber(localData.transitMrn);
      const hasTransitMrnDocument = localData.mrnDocuments.length;

      if (!hasTransitMrnDocument) {
        setFileErrorType(fileErrors.noneSelected);
      }

      if (isValidMrnNumber && hasTransitMrnDocument) {
        stepForward();
      }
    }
  };

  const previousPage = () => {
    if (DDAStatus === 0) {
      // check not preferential origin country shortcode for Swiss and Turkey Norway Liechtenstein Iceland North Macedonia
      if (!['ch', 'tr', 'no', 'is', 'li', 'mk'].includes(localData.exportCountry)) {
        unvisitStep('Preferential Origin');
        stepBackward();
      } else {
        gotoStep('Preferential Origin');
      }
    } else {
      stepBackward();
    }
  };

  const isInitialized = useRef(false);
  const ga = useContext(GoogleAnalyticsContext);

  useEffect(() => {
    if (ga.gaInitialized) {
      if (activeStepKey === 'Transit_MRN' && isInitialized.current === false) {
        isInitialized.current = true;
        window?.gtag?.('event', 'Transit_MRN', {
          event_category: 'EPO',
        });
      }
    }
  }, [activeStepKey, ga.gaInitialized]);

  return (
    <StepWrapper
      stepTitle="Transit MRN"
      stepText="Transit MRN"
      stepKey="Transit_MRN"
      handleNextClick={nextPage}
      handleBackClick={previousPage}
      hideUntilActive
      hideUnlessVisited
    >
      <div className="d-flex step-banner justify-content-center">
        <img
          className="d-flex"
          src={epoTransitMrnPng}
          height="153"
          alt=""
          style={{ borderRadius: '15px', marginTop: '34px', marginBottom: '11px' }}
        />
      </div>

      <div className="d-flex flex-column align-self-center mt-auto pt-2" style={{ maxWidth: '390px' }}>
        <span className="align-self-center pb-3" style={{ fontWeight: '500' }}>
          Are the goods travelling with a Transit MRN?
        </span>
        <div className="d-flex flex-column align-self-center pb-3" style={{ fontWeight: '200' }}>
          <span className="align-self-center text-center" style={{ fontSize: '12px' }}>
            The transit MRN must be a valid 18 character-long reference and accompanied by the Transit Document
          </span>
        </div>
      </div>
      <div className="d-inline-flex justify-content-center gap-3 mt-2">
        <label className="d-inline-flex justify-content-center mt-auto gap-3 cursor-pointer">
          <input
            type="radio"
            name="hasTransitMrn"
            data-testid="transitYes-radio"
            value="yes"
            checked={localData.hasTransitMrn === 'yes'}
            onChange={handleChangeRadio}
          />
          <span>Yes</span>
        </label>
      </div>
      <div className={`expandable-wrapper ${localData.hasTransitMrn === 'yes' && 'is-open'}`}>
        <div className="expandable-inner d-inline-flex justify-content-center">
          <Input
            required
            label="Transit MRN*"
            name="transitMrn"
            type="text"
            value={localData.transitMrn}
            data-testid="transitMrn-textField"
            onChange={(e) => handleChangeMrnNumber(e.target.value)}
            className="pe-2"
            error={mrnNumberError && 'Please enter a valid MRN'}
          />

          <button
            type="button"
            className={`transit-mrn-upload-button ${getUploadButtonSelector()}`}
            data-testid="transitMrnUpload-button"
          >
            <label name="documentUpload">
              <img src={paperclipIcon} alt="Select MRN file" />
              <input
                type="file"
                name="fileuploader"
                onChange={(e) => handleAddFiles(e.target.files)}
                onClick={(e) => {
                  e.target.value = null;
                  setFileErrorType(fileErrors.none);
                }}
                accept=".pdf,.png,.jpeg,.xls,.xlsx"
              />
            </label>
          </button>
        </div>
      </div>
      <div className="d-inline-flex justify-content-center gap-3 mt-2">
        <label className="d-inline-flex justify-content-center gap-3 cursor-pointer">
          <input
            type="radio"
            name="hasTransitMrn"
            data-testid="transitNo-radio"
            value="no"
            checked={localData.hasTransitMrn === 'no'}
            onChange={handleChangeRadio}
          />
          <span>No</span>
        </label>
      </div>
      <div className="px-3 pt-1 mb-3 w-75 mx-auto">
        {fileErrorType !== fileErrors.none && (
          <div className="alert-danger px-3 py-1 w-100 text-center rounded">
            {fileErrorType === fileErrors.noneSelected && 'Transit Document is required'}
            {fileErrorType === fileErrors.fileType && (
              <div className="d-flex flex-column align-self-center ">
                <span>We could not process your file,</span>
                <span>please review the file and file type.</span>
              </div>
            )}
          </div>
        )}
      </div>
    </StepWrapper>
  );
};
